import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import useSheetAnim from '../hooks/useSheetAnim';
import { Chip, Skeletons, RM0 } from './kit';
import { shortAddress } from '../lib/format';
import { commissionColor, commissionTypeLabel, isTenancyLine, ratePct, stateLine } from '../lib/commission';
import { useAuth } from '../context/AuthContext';
import * as salesApi from '../api/sales';

/**
 * Tapping a deal on the Earnings list opens this: how that sale's commission was
 * split, and which slice is the agent's.
 *
 * A bottom sheet rather than a page, and deliberately NOT full height — the list
 * stays visible behind it, so an agent can check one deal and carry on scrolling
 * without a navigation each way. It hugs its content and only starts scrolling
 * internally past 82% of the screen, which a four-line split never reaches.
 *
 * `deal` is the /earnings/by-sale row the card was drawn from, so the header (my
 * amount, my state, the chip) renders instantly and only the split is fetched.
 *
 * A deal the agent only EARNS on — an up-line override from a team member's
 * sale, a line HQ keyed by hand — is one they are not on (`deal.on_sale` false).
 * That sale does not open for them, so the sheet shows their own lines only and
 * offers no way into it: "Open the sale" used to lead straight to "Sale not found".
 */
export default function CommissionSheet({ open, deal, onClose }) {
  const nav = useNavigate();
  const { agent } = useAuth();
  const { mounted, closing } = useSheetAnim(open);
  const [cb, setCb] = useState(null);
  const [failed, setFailed] = useState(false);
  const saleId = deal?.id;
  const rowOffSale = deal?.on_sale === false;

  useEffect(() => {
    if (!open || !saleId) return undefined;
    let live = true;
    setCb(null);
    setFailed(false);
    salesApi.getSalesCommissionBreakdownById(saleId, rowOffSale ? { scope: 'mine' } : {})
      .then((r) => { if (live) setCb(r?.data ?? r); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [open, saleId, rowOffSale]);

  if (!mounted || !deal) return null;

  // The server says so too, for a row drawn before it carried `on_sale`.
  const offSale = rowOffSale || cb?.on_sale === false;

  const items = Array.isArray(cb?.items) ? cb.items : [];
  // Lines HQ keyed by hand sit OUTSIDE net GCI: the server leaves them out of
  // total_amount and the balance, so counting them into "Split between N parties"
  // would put N+1 parties next to a total that only covers N. They get a group
  // and a total of their own instead. Tenancy lines too: they are the tenant's
  // stamping fee, totalled apart as `tenancy_amount`.
  const splitItems = items.filter((l) => !l.is_manual && !isTenancyLine(l));
  const tenancyItems = items.filter(isTenancyLine);
  const manualItems = items.filter((l) => l.is_manual);
  const hasManual = !!cb?.has_manual || manualItems.length > 0;
  const netGci = Number(cb?.net_gci) || 0;
  // Every ringgit of Net GCI is itemised on a normal sale, so the residual is the
  // company's share. ERP-migrated ledgers are verbatim history and were never
  // itemised, which is why the server flags them rather than the client guessing.
  const residual = Number(cb?.balance) || 0;

  const lineRow = (l, i, list) => {
    const mine = l.recipient_type === 'AGENT' && agent?.id && l.recipient_id === agent.id;
    const rate = ratePct(l.rate);
    return (
      <div
        key={l.id || i}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 15px', borderBottom: i < list.length - 1 ? '1px solid var(--border)' : 'none', background: mine ? 'color-mix(in srgb,var(--primary) 7%,transparent)' : 'transparent' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', flex: 'none', background: commissionColor(l.commission_type) }} />
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {commissionTypeLabel(l.commission_type)}{rate ? ` · ${rate}` : ''}
            </span>
            {/* A manual line's description is the only account of what the money
                is for, so it is shown where a computed line has nothing to say. */}
            {l.is_manual && l.description && (
              <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.description}</span>
            )}
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: 'var(--muted)', minWidth: 0 }}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.recipient_name}</span>
              {mine && <Chip label="You" tone="brand" style={{ flex: 'none' }} />}
            </span>
          </span>
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{RM0(l.amount)}</span>
      </div>
    );
  };

  return (
    <div
      onClick={onClose}
      className={`sheet-scrim${closing ? ' is-closing' : ''}`}
      style={{ position: 'fixed', inset: 0, zIndex: 4600, background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`sheet-panel${closing ? ' is-closing' : ''}`}
        style={{ background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '82%', display: 'flex', flexDirection: 'column' }}
      >
        {/* header — drawn from the list row, so it never waits on the network */}
        <div style={{ flex: 'none', padding: '10px 16px 14px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ width: 38, height: 4, borderRadius: 999, background: 'var(--border)', margin: '0 auto 13px' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'ui-monospace,monospace' }}>{deal.sale_ref}</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {shortAddress(deal.property)}
              </div>
            </div>
            <Chip label={deal.chip} />
          </div>
          <div style={{ marginTop: 13 }}>
            <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', color: 'var(--muted)' }}>YOUR COMMISSION</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{RM0(deal.entitled)}</div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1 }}>{stateLine(deal)}</div>
          </div>
        </div>

        <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {cb?.is_preview && (
            <div style={{ background: 'color-mix(in srgb,var(--warn) 8%,var(--surface))', border: '1px solid color-mix(in srgb,var(--warn) 32%,var(--border))', borderRadius: 12, padding: '14px 15px' }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', color: 'var(--warn)' }}>ESTIMATE · NOT FINAL</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, marginTop: 3 }}>
                Worked out from the sale as it stands, so it moves if the price or the split changes.
                It becomes final when HQ raises the invoice.
                {/* The banner speaks for the projected split only — a line HQ keyed
                    by hand is already committed, whatever stage the sale is at. */}
                {hasManual && ' The lines added by HQ below are already set and are not part of this estimate.'}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)' }}>{offSale ? 'YOUR LINES ON THIS DEAL' : 'HOW THIS DEAL SPLITS'}</span>
            {netGci > 0 && (
              <span style={{ fontSize: 11.5, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>Net GCI {RM0(netGci)}</span>
            )}
          </div>

          {failed ? (
            <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
              {offSale
                ? 'Couldn\u2019t load your lines just now. Try again in a moment.'
                : 'Couldn\u2019t load the split just now. Open the sale to see it in full.'}
            </div>
          ) : !cb ? (
            <Skeletons n={3} height={54} />
          ) : (
            <>
              {splitItems.length === 0 ? (offSale && manualItems.length > 0 ? null : (
                <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                  The split is worked out when HQ raises the invoice for this sale. Until then there is
                  nothing to release or pay out{hasManual ? ' apart from the lines HQ added below' : ''}.
                </div>
              )) : (
                <>
                  <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                    {splitItems.map((l, i) => lineRow(l, i, splitItems))}
                  </div>

                  {/* Only your own lines came back, so "split between N parties"
                      would describe a split nobody was shown. */}
                  {!offSale && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 2px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--muted)' }}>
                      <span>Split between {splitItems.length} {splitItems.length === 1 ? 'party' : 'parties'}</span>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{RM0(cb.total_amount)}</span>
                    </div>
                    {Math.abs(residual) >= 0.01 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--muted)' }}>
                        <span>{cb.is_legacy ? 'Company share (not itemised in ERP)' : 'Company share'}</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{RM0(residual)}</span>
                      </div>
                    )}
                  </div>
                  )}
                </>
              )}

              {tenancyItems.length > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
                    <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)' }}>TENANCY FEE</span>
                    <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>Paid by the tenant, outside net GCI</span>
                  </div>
                  <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                    {tenancyItems.map((l, i) => lineRow(l, i, tenancyItems))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--muted)', padding: '0 2px' }}>
                    <span>Tenancy fee total</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {RM0(cb.tenancy_amount ?? tenancyItems.reduce((sum, l) => sum + (Number(l.amount) || 0), 0))}
                    </span>
                  </div>
                </>
              )}

              {manualItems.length > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
                    <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)' }}>ADDED BY HQ</span>
                    <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>Outside net GCI</span>
                  </div>
                  <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                    {manualItems.map((l, i) => lineRow(l, i, manualItems))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--muted)', padding: '0 2px' }}>
                    <span>Added on top of the split</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {RM0(cb.manual_amount ?? manualItems.reduce((sum, l) => sum + (Number(l.amount) || 0), 0))}
                    </span>
                  </div>
                </>
              )}
            </>
          )}

          {offSale && (
            <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5, padding: '0 2px' }}>
              {deal.kind === 'Override' ? 'Override from a team member’s deal. ' : ''}
              You&rsquo;re not on this sale, so it opens for its agents only.
            </div>
          )}
        </div>

        <div style={{ padding: '15px 16px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', flex: 'none' }}>
          {offSale
            ? <Button block onClick={onClose}>Close</Button>
            : <Button block onClick={() => nav(`/sales/${deal.id}`)}>Open the sale ›</Button>}
        </div>
      </div>
    </div>
  );
}
